import React, { useEffect, useId, useRef } from 'react';
import './AddOnQuantityInput.css';
import Togglebox from '../input/Togglebox';

/**
 * Quantity of one sign-up add-on (add form, edit popup): a switch when the add-on allows one per person
 * (maxPerPerson 1 and value ≤ 1), else a stepper 0…max(maxPerPerson, value) — an existing quantity above the
 * limit may stay or decrease, never increase (R8).
 * addOn: { addOnId, name, maxPerPerson }; value: current quantity; onChange(quantity).
 * hint (optional): second line under the name, e.g. price or "noch 2 frei". disabled: whole row locked.
 * max (optional): further upper bound below maxPerPerson, e.g. the remaining stock of the event — '+' stops there;
 * a current quantity above it may stay or decrease. Nothing left to choose (0 and max 0) locks the row.
 * Buttons have type="button" so they never submit a surrounding Popup form.
 */
function AddOnQuantityInput({ addOn, value = 0, onChange, disabled = false, hint, max: limit = null }) {
  const nameId = useId();
  const switchRef = useRef(null);
  const qty = Math.max(0, Math.trunc(Number(value) || 0));
  const maxPerPerson = Math.max(1, Math.trunc(Number(addOn?.maxPerPerson) || 1));
  const cap = limit == null || Number.isNaN(Number(limit)) ? null : Math.max(0, Math.trunc(Number(limit)));
  // Highest quantity '+' may reach; an existing quantity above it may stay (clamp bound = max(upper, qty)).
  const upper = cap == null ? maxPerPerson : Math.min(maxPerPerson, cap);
  const max = Math.max(upper, qty);
  const locked = disabled || (qty === 0 && upper === 0);
  const asSwitch = maxPerPerson === 1 && qty <= 1;
  const name = addOn?.name ?? '';
  const secondLine = hint ?? (asSwitch ? null : `bis ${maxPerPerson} je Person`);

  // Togglebox renders a bare checkbox — name it after the add-on.
  useEffect(() => {
    switchRef.current?.setAttribute('aria-labelledby', nameId);
  }, [asSwitch, nameId]);

  const set = (next) => {
    if (locked) return;
    const clamped = Math.min(Math.max(0, next), max);
    if (clamped !== qty) onChange?.(clamped);
  };

  return (
    <fieldset className={`addOnQuantityInput${qty > 0 ? ' isActive' : ''}`} disabled={locked}>
      <span className="addOnQuantityText">
        <span className="addOnQuantityName" id={nameId}>{name}</span>
        {secondLine && <span className="addOnQuantityHint">{secondLine}</span>}
      </span>
      {asSwitch ? (
        <Togglebox ref={switchRef} checked={qty === 1} onChange={e => set(e.target.checked ? 1 : 0)} />
      ) : (
        <span className="addOnQuantityStepper">
          <button
            type="button"
            className="addOnQuantityBtn"
            onClick={() => set(qty - 1)}
            disabled={locked || qty === 0}
            aria-label={`${name}: eins weniger`}
          >
            <span className="material-symbols-rounded" aria-hidden="true">remove</span>
          </button>
          <output className="addOnQuantityValue" aria-live="polite" aria-label={`${name}: Menge`}>{qty}</output>
          <button
            type="button"
            className="addOnQuantityBtn"
            onClick={() => set(qty + 1)}
            disabled={locked || qty >= upper}
            aria-label={`${name}: eins mehr`}
          >
            <span className="material-symbols-rounded" aria-hidden="true">add</span>
          </button>
        </span>
      )}
    </fieldset>
  );
}

export default AddOnQuantityInput;
